import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Volume2, User, Users } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// OpenAI TTS voices
const VOICES = [
  { value: "alloy", label: "Alloy", description: "Neutral and balanced" },
  { value: "echo", label: "Echo", description: "Clear and articulate" },
  { value: "fable", label: "Fable", description: "Expressive British accent" },
  { value: "onyx", label: "Onyx", description: "Deep and authoritative" },
  { value: "nova", label: "Nova", description: "Warm and friendly" },
  { value: "shimmer", label: "Shimmer", description: "Soft and gentle" },
];

interface Character {
  name: string;
  voice: string;
  color?: string;
}

interface CharacterMap {
  narrator?: string;
  characters?: Character[];
}

interface VoiceAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storyId: number;
  storyContent: string;
  currentCharacterMap?: CharacterMap;
}

export function VoiceAssignmentDialog({
  open,
  onOpenChange,
  storyId,
  storyContent,
  currentCharacterMap,
}: VoiceAssignmentDialogProps) {
  const { toast } = useToast();
  const [narratorVoice, setNarratorVoice] = useState<string>("nova");
  const [characters, setCharacters] = useState<Character[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);

  // Reset state when dialog opens with different story
  useEffect(() => {
    if (open) {
      // Reset to saved values or defaults
      setNarratorVoice(currentCharacterMap?.narrator || "nova");
      detectCharacters();
    }
  }, [open, storyId, currentCharacterMap]);

  const detectCharacters = () => {
    setIsDetecting(true);
    
    // Character detection from dialogue patterns
    // Look for quoted speech with character names (handling various punctuation)
    const characterNames = new Set<string>();
    
    // Pattern 1: "dialogue" said/asked CharacterName
    const dialoguePattern = /[""]([^""]+)[""][,\s]*(?:said|asked|replied|whispered|shouted|exclaimed|called|answered|cried|yelled|muttered|murmured)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/gi;
    let match;
    while ((match = dialoguePattern.exec(storyContent)) !== null) {
      characterNames.add(match[2].trim());
    }
    
    // Pattern 2: CharacterName said/asked: "dialogue" (with optional colon, punctuation, and spaces)
    const reversePattern = /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s+(?:said|asked|replied|whispered|shouted|exclaimed|called|answered|cried|yelled|muttered|murmured)[\s:]*[""]([^""]+)[""][\s,.!?;:-]*/gi;
    while ((match = reversePattern.exec(storyContent)) !== null) {
      characterNames.add(match[1].trim());
    }
    
    // Pattern 3: CharacterName's possessive dialogue tags
    const possessivePattern = /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)'s\s+(?:voice|eyes|face|heart)/gi;
    while ((match = possessivePattern.exec(storyContent)) !== null) {
      characterNames.add(match[1].trim());
    }

    // Merge with existing characters from saved characterMap
    const existingCharacters = currentCharacterMap?.characters || [];
    const allCharacterNames = new Set([
      ...Array.from(characterNames),
      ...existingCharacters.map(c => c.name)
    ]);

    // Create character list with default or saved voices
    const detectedCharacters: Character[] = Array.from(allCharacterNames).map((name, index) => {
      // Check if we have an existing voice assignment
      const existing = existingCharacters.find(c => c.name === name);
      return {
        name,
        voice: existing?.voice || VOICES[index % VOICES.length].value,
        color: existing?.color,
      };
    });

    setCharacters(detectedCharacters);
    setIsDetecting(false);
  };

  const updateCharacterVoice = (characterName: string, voice: string) => {
    setCharacters(chars =>
      chars.map(char =>
        char.name === characterName ? { ...char, voice } : char
      )
    );
  };

  const saveVoiceAssignments = useMutation({
    mutationFn: async () => {
      const characterMap: CharacterMap = {
        narrator: narratorVoice,
        characters: characters.length > 0 ? characters : undefined,
      };

      const response = await apiRequest('PATCH', `/api/stories/${storyId}/voices`, {
        characterMap,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/stories'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stories', storyId] });
      toast({
        title: "Voices assigned",
        description: "Voice assignments have been saved successfully.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save voice assignments",
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="dialog-voice-assignment">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Volume2 className="h-5 w-5" />
            Voice Assignment
          </DialogTitle>
          <DialogDescription>
            Assign different voices to the narrator and characters for dynamic storytelling
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Narrator Voice */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-base font-semibold">
              <User className="h-4 w-4" />
              Narrator Voice
            </Label>
            <Select value={narratorVoice} onValueChange={setNarratorVoice}>
              <SelectTrigger data-testid="select-narrator-voice">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VOICES.map((voice) => (
                  <SelectItem key={voice.value} value={voice.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{voice.label}</span>
                      <span className="text-xs text-muted-foreground">{voice.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Character Voices */}
          {isDetecting ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-gray-600">Detecting characters...</span>
            </div>
          ) : characters.length > 0 ? (
            <div className="space-y-3">
              <Label className="flex items-center gap-2 text-base font-semibold">
                <Users className="h-4 w-4" />
                Character Voices ({characters.length})
              </Label>
              <div className="space-y-3">
                {characters.map((character) => (
                  <div key={character.name} className="flex items-center gap-3">
                    <div className="w-32 font-medium text-sm">{character.name}</div>
                    <Select
                      value={character.voice}
                      onValueChange={(voice) => updateCharacterVoice(character.name, voice)}
                    >
                      <SelectTrigger data-testid={`select-character-voice-${character.name}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VOICES.map((voice) => (
                          <SelectItem key={voice.value} value={voice.value}>
                            <div className="flex flex-col">
                              <span className="font-medium">{voice.label}</span>
                              <span className="text-xs text-muted-foreground">{voice.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No characters with dialogue detected in this story.</p>
              <p className="text-xs mt-1">The narrator voice will be used for the entire story.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saveVoiceAssignments.isPending}
            data-testid="button-cancel-voice-assignment"
          >
            Cancel
          </Button>
          <Button
            onClick={() => saveVoiceAssignments.mutate()}
            disabled={saveVoiceAssignments.isPending}
            data-testid="button-save-voice-assignment"
          >
            {saveVoiceAssignments.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Voice Assignments'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
